// SPDX-FileCopyrightText: Copyright (c) 2026 NVIDIA CORPORATION & AFFILIATES. All rights reserved.
// SPDX-License-Identifier: Apache-2.0

import type { PropertyValues } from 'lit';
import { html, nothing } from 'lit';
import { property } from 'lit/decorators/property.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import {
  focusElementTimeout,
  onListboxActivate,
  useStyles,
  i18n,
  I18nController,
  onChildListMutation,
  getElementUpdate,
  scopedRegistry,
  appendRootNodeStyle
} from '@nvidia-elements/core/internal';
import { Control } from '@nvidia-elements/core/forms';
import { Icon } from '@nvidia-elements/core/icon';
import { Menu, MenuItem } from '@nvidia-elements/core/menu';
import { Dropdown } from '@nvidia-elements/core/dropdown';
import { Tag } from '@nvidia-elements/core/tag';
import styles from './select.css?inline';
import globalStyles from './select.global.css?inline';

/**
 * @element nve-select
 * @description A select is a control that enables users to select an option from a list of options.
 * @documentation https://nvidia.github.io/elements/docs/elements/select/
 * @since 0.3.0
 * @entrypoint \@nvidia-elements/core/select
 * @cssprop --padding
 * @cssprop --font-size
 * @cssprop --height
 * @cssprop --background
 * @cssprop --color
 * @cssprop --border-radius
 * @cssprop --border
 * @cssprop --border-bottom
 * @cssprop --cursor
 * @cssprop --font-weight
 * @cssprop --text-align
 * @cssprop --scroll-height
 * @cssprop --width
 * @cssprop --control-width
 * @cssprop --max-width
 * @cssprop --min-width
 * @cssprop --box-shadow
 * @cssprop --max-height
 * @cssprop --text-transform
 * @csspart tag
 * @csspart menu - The dropdown menu element
 * @csspart menu-item - The menu item elements
 * @csspart icon - The icon element
 * @csspart caret - The caret/chevron icon element
 * @csspart dropdown - The dropdown popup element
 * @slot - Select input element
 * @aria https://developer.mozilla.org/en-US/docs/Web/HTML/Element/select
 */
@i18n<Select>()
@scopedRegistry()
export class Select extends Control {
  static styles = useStyles([...Control.styles, styles]);

  static readonly metadata = {
    tag: 'nve-select',
    version: '0.0.0'
  };

  static elementDefinitions = {
    [Icon.metadata.tag]: Icon,
    [Dropdown.metadata.tag]: Dropdown,
    [Menu.metadata.tag]: Menu,
    [MenuItem.metadata.tag]: MenuItem,
    [Tag.metadata.tag]: Tag
  };

  /** Flat container option applies when embedding the component within another containing element */
  @property({ type: String, reflect: true }) container?: 'flat' | 'inline';

  #i18nController: I18nController<this> = new I18nController<this>(this);

  /** Updates internal string values for internationalization. */
  @property({ type: Object }) i18n = this.#i18nController.i18n;

  get #select() {
    return this.input as unknown as HTMLSelectElement;
  }

  get #options() {
    return Array.from(this.#select?.options ? this.#select.options : []);
  }

  get #dropdown() {
    return this.shadowRoot!.querySelector<Dropdown>(Dropdown.metadata.tag);
  }

  get #menuItems() {
    return this.shadowRoot!.querySelectorAll<MenuItem>(MenuItem.metadata.tag);
  }

  get #input() {
    return this.shadowRoot!.querySelector<HTMLElement>('[input]');
  }

  get #tags() {
    return this.shadowRoot!.querySelector('.tags');
  }

  get #caret() {
    return this.shadowRoot!.querySelector<Icon>('[name="caret"]');
  }

  get #placeholderOption() {
    return this.#options.find(o => o.selected && o.hidden && o.disabled);
  }

  #observers: (MutationObserver | ResizeObserver)[] = [];

  get #multipleSelectLabel() {
    const option = this.#placeholderOption;
    const label = option ? option.innerText : `${this.#select.selectedOptions.length} ${this.i18n.selected}`;
    return html`<div class="tags-label ${option ? 'placeholder' : ''}" aria-hidden="true">${label}</div>`;
  }

  protected get prefixContent() {
    return this.#select?.multiple && this.#select?.size === 0
      ? html`
    ${this.#multipleSelectLabel}
    <div class="tags">
      ${this.#options
        .filter(o => o.selected && o !== this.#placeholderOption)
        .map(
          o => html`
      <nve-tag part="tag" readonly color="gray-slate" closable .value=${o.value} @click=${() => this.#selectValue(o, false)}>${o.textContent}</nve-tag>`
        )}
    </div>`
      : nothing;
  }

  get #menu() {
    return html`
    <nve-menu part="menu" role="listbox" style="--width: 100%; --min-width: fit-content" aria-label=${ifDefined(this.i18n.select)}>
      ${this.#options
        .filter(o => o !== this.#placeholderOption)
        .map(
          (o, i) => html`
      <nve-menu-item part="menu-item" role="option" @click=${() => this.#selectValue(o, !o.selected)} ?selected=${o.selected} ?disabled=${o.disabled} ?hidden=${!!o.hidden} aria-selected=${o.selected}>
        <slot name="option-${i + 1}">
          <nve-icon part="icon" name="check" size="sm" aria-hidden="true"></nve-icon> ${o.innerText}
        </slot>
      </nve-menu-item>`
        )}
    </nve-menu>`;
  }

  protected get suffixContent() {
    return this.#select?.size === 0
      ? html`
      <nve-icon name="caret" part="caret" direction="down" size="sm" aria-hidden="true"></nve-icon>
      <nve-dropdown part="dropdown" @close=${this.#closeDropdown} @open=${this.#openDropdown} hidden .anchor=${this.#input as HTMLElement} position="bottom">
        ${this.#menu}
      </nve-dropdown>`
      : this.#menu;
  }

  async firstUpdated(props: PropertyValues<this>) {
    super.firstUpdated(props);
    await this.updateComplete;
    this.#setupCustomSelectUI();
  }

  #syncSelectValueStates() {
    this.#observers.push(
      getElementUpdate(this.#select, 'value', () => this.requestUpdate()),
      onChildListMutation(this.#select, () => this.requestUpdate(), { attributes: true, subtree: true }),
      onChildListMutation(this.#select, () => this.#syncOptionSelectedStates(), { subtree: true })
    );
  }

  #optionObservers = new Map<HTMLOptionElement, MutationObserver>();

  #setupObservers() {
    this.#disconnectObservers();
    this.#setupOverflowListener();
    this.#syncSelectValueStates();
    this.#syncOptionSelectedStates();
  }

  #disconnectObservers() {
    this.#observers.forEach(observer => observer.disconnect());
    this.#observers = [];
    this.#optionObservers.clear();
  }

  #syncOptionSelectedStates() {
    const options = new Set(this.#options);

    this.#optionObservers.forEach((observer, option) => {
      if (!options.has(option)) {
        observer.disconnect();
        this.#optionObservers.delete(option);
        this.#observers = this.#observers.filter(o => o !== observer);
      }
    });

    this.#options.forEach(o => {
      if (!this.#optionObservers.has(o)) {
        const observer = getElementUpdate(o, 'selected', () => this.requestUpdate());
        this.#optionObservers.set(o, observer);
        this.#observers.push(observer);
      }
    });
  }

  connectedCallback() {
    super.connectedCallback();
    appendRootNodeStyle(this, globalStyles);
    void this.updateComplete.then(() => {
      if (this.isConnected) {
        this.#setupObservers();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#disconnectObservers();
  }

  async updated(props: PropertyValues<this>) {
    super.updated(props);
    if (this.#select?.size && this.#select?.size !== 0) {
      this._internals.states.add('size');
      this.style.setProperty('--size', `${this.#select.size}`);
    } else {
      this._internals.states.delete('size');
      this.style.removeProperty('--size');
    }

    if (this.#select?.multiple && this.#select?.size === 0) {
      this._internals.states.add('multiple');
    } else {
      this._internals.states.delete('multiple');
    }
  }

  #setupCustomSelectUI() {
    if (this.#select?.size === 0) {
      onListboxActivate(this.#select, () => {
        this.requestUpdate(); // update menu items
        this.#dropdown!.style.setProperty('--min-width', `${this.#input!.getBoundingClientRect().width}px`);
        this.#dropdown!.showPopover();
        focusElementTimeout(this.#menuItems[0] as HTMLElement);
      });
    }
  }

  #openDropdown() {
    this.#dropdown!.hidden = false;
    this.#caret!.direction = 'up';
  }

  #closeDropdown() {
    this.#dropdown!.hidden = true;
    this.#caret!.direction = 'down';
  }

  #setupOverflowListener() {
    const observer = new ResizeObserver(entries => this.#updateMultipleOverflow(entries[0]!.contentRect.width));
    this.#observers.push(observer);
    observer.observe(this.#input!);
  }

  async #selectValue(option: HTMLOptionElement, selected: boolean) {
    if (this.#select.multiple || (!this.#select.multiple && option.value !== this.#select.value)) {
      option.selected = selected;
    }

    // native select only the first selected option determines the select input value
    if (this.#select.selectedOptions.length < 1) {
      this.#select.value = selected ? option.value : '';
    }

    this.#select.dispatchEvent(new Event('input', { bubbles: true }));
    this.#select.dispatchEvent(new Event('change', { bubbles: true }));
    this.requestUpdate();

    if (!this.#select.multiple && this.#dropdown) {
      this.#dropdown.hidePopover();
    } else {
      await this.updateComplete;
      this.#updateMultipleOverflow(this.#input!.getBoundingClientRect().width);
    }
  }

  #updateMultipleOverflow(width: number) {
    const tags = this.#tags;
    if (this.#select?.multiple && tags && tags.getBoundingClientRect().width > width - 48) {
      this._internals.states.add('multiple-overflow');
    } else {
      this._internals.states.delete('multiple-overflow');
    }
  }
}
